import { Checkbox, Form, Input } from "antd";
import { AwsRegionSelect } from "@/common/aws";
import type { EnvironmentProvider } from "@/common/model";
import { PROVIDER_METADATA_FIELDS } from "./environment-base";

/** One entry of {@link PROVIDER_METADATA_FIELDS}. */
type MetadataField = NonNullable<
	(typeof PROVIDER_METADATA_FIELDS)[string]
>[number];

/**
 * Renders a single provider-metadata field (`meta_<key>`) as a Form.Item: a text input, an
 * `AwsRegionSelect` for region fields, or a password input for write-only secrets. In edit mode a
 * stored secret gets a "Remove saved value" toggle that disables the input and clears it on save.
 * Shared by the onboarding wizard and the edit wizard so the field rules stay in one place.
 */
export function MetadataFieldItem({
	field,
	isEditMode = false,
	configuredSecret = false,
	required,
}: {
	field: MetadataField;
	isEditMode?: boolean;
	/** Edit mode: this secret already has a stored value → offer a clear toggle. */
	configuredSecret?: boolean;
	/** Overrides `field.required` (e.g. External ID is required when connecting AWS roles manually). */
	required?: boolean;
}) {
	const form = Form.useFormInstance();
	// Call the hook unconditionally (rules of hooks); only a configured secret can be "cleared".
	const clearRequested = Form.useWatch(`clear_${field.key}`, form);
	const clearing = configuredSecret && Boolean(clearRequested);
	const isRequired = required ?? field.required;
	return (
		<div>
			<Form.Item
				name={`meta_${field.key}`}
				label={field.label}
				extra={field.description}
				// Region fields start on the placeholder region instead of empty; edit mode
				// overwrites this with the stored value when the wizard opens.
				initialValue={field.type === "region" ? field.placeholder : undefined}
				rules={[
					...(isRequired && !clearing ? [{ required: true }] : []),
					...(field.pattern
						? [
								{
									pattern: field.pattern,
									message: field.patternMessage ?? "Enter a valid AWS ARN",
								},
							]
						: []),
				]}
			>
				{field.type === "region" ? (
					<AwsRegionSelect />
				) : field.secret ? (
					<Input.Password
						disabled={clearing}
						placeholder={
							isEditMode ? "leave blank to keep current" : field.placeholder
						}
					/>
				) : (
					<Input maxLength={field.maxLength} placeholder={field.placeholder} />
				)}
			</Form.Item>
			{configuredSecret && (
				<Form.Item
					name={`clear_${field.key}`}
					valuePropName="checked"
					initialValue={false}
				>
					<Checkbox>Remove saved value</Checkbox>
				</Form.Item>
			)}
		</div>
	);
}

/**
 * Collects `meta_<key>` form values into the metadata key-value pairs the API expects. Empty fields
 * are omitted (so a blank secret keeps the stored value); in edit mode an explicit `clear_<key>`
 * toggle emits an empty value to unset a stored secret.
 */
export function collectMetadata(
	vals: Record<string, unknown>,
	{
		provider,
		isEditMode,
	}: { provider: EnvironmentProvider; isEditMode: boolean },
): Array<{ key: string; value: string }> {
	const fields = PROVIDER_METADATA_FIELDS[provider] ?? [];
	const result: Array<{ key: string; value: string }> = [];
	for (const field of fields) {
		if (field.secret && isEditMode && vals[`clear_${field.key}`]) {
			result.push({ key: field.key, value: "" });
			continue;
		}
		const value =
			(vals[`meta_${field.key}`] as string | undefined)?.trim() ?? "";
		if (value.length > 0) result.push({ key: field.key, value });
	}
	return result;
}
